/**
 * Коды разрешений (И-7): проверка прав по permission, не по имени роли.
 * Произвольные роли создаются в админке без изменения кода (E03).
 */
export const PERMISSIONS = {
  PRICING_GLOBAL_UPDATE: "pricing.global.update",
  PRICING_GROUP_UPDATE: "pricing.group.update",
  PRICING_CLIENT_MARKUP_UPDATE: "pricing.client_markup.update",
  TRACTION_ACCESS_APPROVE: "traction_access.approve",
  BOM_UPLOAD: "bom.upload",
  REFERENCE_MANAGE: "reference.manage",
  AUDIT_VIEW: "audit.view",
  CLIENT_DELETE: "client.delete",
  /** Доступ к разделу «Настройка прав» (ФТ-6.8); сверх минимума разд. 6.1. */
  PERMISSIONS_MANAGE: "permissions.manage",
  /** Смена темы оформления (ФТ-8.3); модератор дизайна. */
  THEME_MANAGE: "theme.manage",
} as const;

/** Тип кода разрешения из каталога. */
export type PermissionCode = (typeof PERMISSIONS)[keyof typeof PERMISSIONS];

/** Список всех известных кодов разрешений. */
export const PERMISSION_CODES: readonly PermissionCode[] = Object.values(PERMISSIONS);

/** Минимальный набор разрешений, прямо перечисленный в разд. 6.1 ЧТЗ. */
export const CHTZ_MINIMUM_PERMISSION_CODES: readonly PermissionCode[] = [
  PERMISSIONS.PRICING_GLOBAL_UPDATE,
  PERMISSIONS.PRICING_GROUP_UPDATE,
  PERMISSIONS.PRICING_CLIENT_MARKUP_UPDATE,
  PERMISSIONS.TRACTION_ACCESS_APPROVE,
  PERMISSIONS.BOM_UPLOAD,
  PERMISSIONS.REFERENCE_MANAGE,
  PERMISSIONS.AUDIT_VIEW,
  PERMISSIONS.CLIENT_DELETE,
];

/** Раздел каталога прав — группировка для экрана «Настройка прав». */
export type PermissionSection = "pricing" | "catalog" | "clients" | "administration" | "design";

/** Описание разрешения для интерфейса: код, подпись, раздел. */
export type PermissionDescriptor = {
  code: PermissionCode;
  title: string;
  section: PermissionSection;
};

/**
 * Каталог разрешений с подписями — единый источник для API и админки.
 * Экран «Настройка прав» строится по нему, а не по жёсткой вёрстке (ФТ-6.8).
 */
export const PERMISSION_CATALOG: readonly PermissionDescriptor[] = [
  {
    code: PERMISSIONS.PRICING_GLOBAL_UPDATE,
    title: "Изменение глобального коэффициента цены",
    section: "pricing",
  },
  {
    code: PERMISSIONS.PRICING_GROUP_UPDATE,
    title: "Изменение коэффициентов по группам конвейеров",
    section: "pricing",
  },
  {
    code: PERMISSIONS.PRICING_CLIENT_MARKUP_UPDATE,
    title: "Персональная надбавка клиента",
    section: "pricing",
  },
  {
    code: PERMISSIONS.TRACTION_ACCESS_APPROVE,
    title: "Выдача допуска к тяговому расчёту",
    section: "clients",
  },
  { code: PERMISSIONS.CLIENT_DELETE, title: "Удаление клиентов", section: "clients" },
  {
    code: PERMISSIONS.BOM_UPLOAD,
    title: "Загрузка комплектовочных ведомостей",
    section: "catalog",
  },
  {
    code: PERMISSIONS.REFERENCE_MANAGE,
    title: "Управление справочниками и методиками",
    section: "catalog",
  },
  { code: PERMISSIONS.AUDIT_VIEW, title: "Просмотр журнала аудита", section: "administration" },
  {
    code: PERMISSIONS.PERMISSIONS_MANAGE,
    title: "Настройка прав и ролей",
    section: "administration",
  },
  {
    code: PERMISSIONS.THEME_MANAGE,
    title: "Смена темы оформления",
    section: "design",
  },
];

/** Проверяет, что код входит в каталог разрешений. */
export function isPermissionCode(value: string): value is PermissionCode {
  return (PERMISSION_CODES as readonly string[]).includes(value);
}
